import _ from 'lodash'
import Mustache from 'mustache'

import MESSAGES from 'core/data-model/constants/validation-messages'
import { createChecker, valid } from 'core/service/lib/check'

const disallowedYearForLumpSum = [1]
const defaultLumpSum = { value: 0, isValid: true }

export const disallowLumpSumInputByYear = (year, lumpSum) => {
  const isInDisallowedYear = disallowedYearForLumpSum.includes(year)
  const isCurrentLumpSumDisallowed = lumpSum === 'DISALLOWED'

  return isInDisallowedYear ? 'DISALLOWED' : isCurrentLumpSumDisallowed ? defaultLumpSum : lumpSum
}

export const lumpSumRPULChecker = createChecker([
  [
    'lessThanMinimum',
    {
      type: 'VALIDATION',
      rule: (_, lumpSum) => lumpSum >= 10000,
      message: Mustache.render(MESSAGES.RULE_IWEALTHY_LUMP_SUM_LESS_THAN_MINIMUM, { lumpSum: '10,000' }),
    },
  ],
  [
    'moreThanMaximum',
    {
      type: 'VALIDATION',
      rule: ({ annualRegularPremium }, lumpSum) => lumpSum <= 5 * annualRegularPremium,
      message: MESSAGES.RULE_IWEALTHY_LUMP_SUM_MORE_THAN_MAXIMUM,
    },
  ],
  [
    'moreThanTotalLumpSumMaximum',
    {
      type: 'VALIDATION',
      rule: ({ totalLumpSumAmount }, lumpSum) => totalLumpSumAmount <= 150000000,
      message: MESSAGES.RULE_IWEALTHY_LUMP_SUM_MORE_THAN_TOTAL_MAXIMUM,
    },
  ],
])

export const lumpSumSPULChecker = createChecker([
  [
    'lessThanMinimum',
    {
      type: 'VALIDATION',
      rule: (_, lumpSum) => lumpSum >= 5000,
      message: Mustache.render(MESSAGES.RULE_IINVEST_LUMP_SUM_LESS_THAN_MINIMUM, { lumpSum: '5,000' }),
    },
  ],
  [
    'moreThanMaximum',
    {
      type: 'VALIDATION',
      rule: ({ annualRegularPremium }, lumpSum) => lumpSum <= 10 * annualRegularPremium,
      message: MESSAGES.RULE_IINVEST_LUMP_SUM_MORE_THAN_MAXIMUM,
    },
  ],
])

export const lumpSumRPUDRChecker = createChecker([
  [
    'lessThanMinimum',
    {
      type: 'VALIDATION',
      rule: (_, lumpSum) => lumpSum >= 10000,
      message: MESSAGES.RULE_RPUDR_LUMP_SUM_LESS_THAN_MINIMUM,
    },
  ],
])

export const updateLumpSumByYear = (lumpSums, lumpSumToUpdate) => {
  return lumpSums.map((lumpSum) => (lumpSum.year === lumpSumToUpdate.year ? lumpSumToUpdate : lumpSum))
}

/**
 * @param dataWrapper {ReturnType<import('core/data/data-wrapper')._DB>}
 */
export function createInvestmentCoverageLumpsumService(dataWrapper) {
  const totalLumpSum = (lumpSums) => _.sumBy(lumpSums, 'value')

  const validateLumpSumRPUL = async (regularPremium, lumpSums, modelFactorId) => {
    let modelFactor = await dataWrapper.getModelFactorById(modelFactorId)
    return _validateLumpSum(regularPremium, lumpSums, modelFactor.periods, lumpSumRPULChecker)
  }

  const validateLumpSumSPUL = async (regularPremium, lumpSums, modelFactorId) => {
    let modelFactor = await dataWrapper.getModelFactorById(modelFactorId)
    return _validateLumpSum(regularPremium, lumpSums, modelFactor.periods, lumpSumSPULChecker)
  }

  const validateLumpSumRPUDR = async (regularPremium, lumpSums, modelFactorId) => {
    let modelFactor = await dataWrapper.getModelFactorById(modelFactorId)
    return _validateLumpSum(regularPremium, lumpSums, modelFactor.periods, lumpSumRPUDRChecker)
  }

  const _validateLumpSum = (regularPremium, lumpSums, paymentFrequency, lumpSumChecker) => {
    const totalLumpSumAmount = totalLumpSum(lumpSums)
    const annualRegularPremium = regularPremium * paymentFrequency
    const validatedEach = lumpSums.map(({ year, value }) => {
      const checked = value === 0 ? valid(value) : lumpSumChecker({ annualRegularPremium, totalLumpSumAmount }, value)

      const checkedLumpSumByYear = { year, value, isValid: checked.isOk }
      const errors = checked.getErrorMessages()

      return { value: checkedLumpSumByYear, errors }
    })

    const allErrors = validatedEach.reduce((acc, { errors }) => acc.concat(errors), [])

    return {
      values: validatedEach.map(({ value }) => value),
      errors: _.uniq(allErrors),
    }
  }

  return {
    totalLumpSum,
    validateLumpSumRPUL,
    validateLumpSumSPUL,
    validateLumpSumRPUDR,
    _validateLumpSum,
  }
}
